import { BookOpenCheck, ExternalLink } from "lucide-react";

type EvidenceReference = {
  title: string;
  url: string;
};

type EvidenceSourceContextProps = {
  references: EvidenceReference[];
};

/**
 * Provides a visible, qualified source context for mapped topic clusters.
 * References add context for the analysis; they do not turn every article
 * statement into an independently verified claim.
 */
export default function EvidenceSourceContext({ references }: EvidenceSourceContextProps) {
  if (references.length === 0) return null;

  return (
    <section className="evidence-source-context mt-12 border-t border-border pt-8" aria-labelledby="evidence-source-heading" data-testid="evidence-source-context">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-amber-700 dark:text-amber-400">
        <BookOpenCheck className="h-4 w-4" aria-hidden="true" />
        Evidence and source context
      </div>
      <h2 id="evidence-source-heading" className="mt-3 font-serif text-2xl text-foreground">Sources to review alongside this analysis</h2>
      <p className="mt-3 max-w-3xl text-sm leading-7 text-muted-foreground">
        These resources provide topic-level context for the article. Review the original materials for their own scope, methods and updates before applying an insight to a commercial decision.
      </p>
      <ul className="mt-5 space-y-2">
        {references.map((reference) => (
          <li key={reference.url}>
            <a
              href={reference.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-amber-700 hover:text-amber-800 hover:underline dark:text-amber-400 dark:hover:text-amber-300"
            >
              {reference.title} <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
